import { useMemo, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Select } from "@/components/ui/select"
import { extend, fmtWhatIf, gmPct, lotTotal } from "@/lib/bid-what-if"
import { EM_DASH } from "@/lib/money"
import type { MatrixCategoryWire, MatrixRow } from "@/types/bids"

type Pick = { categoryKey: string; levelId: number } | null

/** The mock's Room-selector view: pick a category + level per room and
 *  watch the price, GM pill, and lot total move live. Pure client state —
 *  explicitly a what-if, nothing persists; the authoritative numbers stay
 *  the full matrix's server cells. */
export function RoomSelectorView({
  planId,
  rows,
  categories,
  wasteIn,
}: {
  planId: number
  rows: MatrixRow[]
  categories: MatrixCategoryWire[]
  wasteIn: boolean
}) {
  const [picks, setPicks] = useState<Map<string, Pick>>(new Map())

  const categoriesByKey = useMemo(
    () => new Map(categories.map((c) => [c.key as string, c])),
    [categories]
  )

  const areaRows = rows.filter((r) => r.area && r.qty_f != null)

  const lines = areaRows.map((row) => {
    const pick = picks.get(row.client_id) ?? null
    const category = pick ? categoriesByKey.get(pick.categoryKey) : undefined
    const level = category?.levels.find((l) => l.id === pick?.levelId)
    // Waste-in builders price the inflated quantity (per the picked
    // category's waste %) — same figure the matrix cells multiply.
    const wasteFactor =
      wasteIn && category ? 1 + parseFloat(category.waste_pct) / 100 : 1
    const pricedQty = Math.round((row.qty_f ?? 0) * wasteFactor * 100) / 100
    const qtySf = row.unit === "SY" ? pricedQty * 9 : pricedQty
    const price = level ? extend(qtySf, level.rate_sf_f) : null
    // GM costs the NET area: COGS already bought the waste, and the price
    // above bills it — same M10 correction as Bids::Pricing's blended GM.
    const netSf = row.unit === "SY" ? (row.qty_f ?? 0) * 9 : (row.qty_f ?? 0)
    const gm = level && price != null ? gmPct(price, level.cogs_rate_f, netSf) : null
    return { row, pick, category, level, pricedQty, price, gm }
  })

  const total = lotTotal(lines.map((l) => l.price ?? 0))

  const setPick = (clientId: string, pick: Pick) =>
    setPicks((prev) => {
      const next = new Map(prev)
      next.set(clientId, pick)
      return next
    })

  return (
    <div className="mt-4 space-y-3" data-testid="room-selector">
      <div className="callout">
        <p>
          What-if only — nothing here is saved. Rates and cost blends come from the workspace
          levels; use the full matrix&apos;s ✓ to set the real base bid.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-hairline bg-surface">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-hairline text-left align-bottom text-xs uppercase tracking-wide text-ink-muted">
              <th className="px-3 pb-1.5 pt-2 font-semibold">Room</th>
              <th className="px-3 pb-1.5 pt-2 text-right font-semibold">SF</th>
              <th className="px-3 pb-1.5 pt-2 font-semibold">Category</th>
              <th className="px-3 pb-1.5 pt-2 font-semibold">Level</th>
              <th className="px-3 pb-1.5 pt-2 text-right font-semibold">Price</th>
              <th className="px-3 pb-1.5 pt-2 text-right font-semibold">GM</th>
            </tr>
          </thead>
          <tbody>
            {lines.map(({ row, pick, category, level, pricedQty, price, gm }) => (
              <tr key={row.client_id} className="border-b border-hairline">
                <td className="px-3 py-2 text-ink-display">
                  {row.name}
                  {row.multiplier > 1 && <span className="text-ink-muted"> ×{row.multiplier}</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
                  {pricedQty.toFixed(2)}
                </td>
                <td className="px-3 py-2">
                  <Select
                    aria-label={`${row.name} category`}
                    value={pick?.categoryKey ?? ""}
                    onChange={(e) => {
                      const key = e.target.value
                      const cat = categoriesByKey.get(key)
                      setPick(
                        row.client_id,
                        cat ? { categoryKey: key, levelId: cat.levels[0]!.id } : null
                      )
                    }}
                    className="w-36"
                    data-testid={`selector-category-${row.client_id}`}
                  >
                    <option value="">— None —</option>
                    {categories.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="px-3 py-2">
                  <Select
                    aria-label={`${row.name} level`}
                    value={pick ? String(pick.levelId) : ""}
                    onChange={(e) =>
                      pick && setPick(row.client_id, { ...pick, levelId: Number(e.target.value) })
                    }
                    disabled={!pick}
                    className="w-44"
                    data-testid={`selector-level-${row.client_id}`}
                  >
                    {!pick && <option value="">—</option>}
                    {category?.levels.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} · ${l.rate}/{l.unit_label}
                      </option>
                    ))}
                  </Select>
                </td>
                <td
                  className="px-3 py-2 text-right tabular-nums"
                  data-testid={`selector-price-${row.client_id}`}
                >
                  {price == null ? EM_DASH : fmtWhatIf(price)}
                </td>
                <td className="px-3 py-2 text-right" data-testid={`selector-gm-${row.client_id}`}>
                  {gm == null ? (
                    EM_DASH
                  ) : (
                    <Badge tone={level && gm >= level.target_gm_f ? "accent" : "signal"}>
                      {gm.toFixed(1)}%
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-ink-muted font-semibold">
              <td className="px-3 py-2" colSpan={4}>
                Lot total (what-if)
              </td>
              <td className="px-3 py-2 text-right tabular-nums" data-testid="selector-total">
                {fmtWhatIf(total)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      {/* key the state to the plan so switching plans resets the toy */}
      <span className="hidden" data-plan={planId} />
    </div>
  )
}
